import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { SessionTagEnum, SessionTagInput } from "@modules/programSession/components/sessionTagInput"
import {
  ProgramInfiniteSessionFormikType,
  ProgramSessionInputKey,
} from "@modules/programSession/programSessionFormikType"
import { AddBlock } from "@modules/sessionBlock/components/addBlock"
import { ExistingBlock } from "@modules/sessionBlock/components/existingBlock"
import { GetNewExerciseDraft } from "@modules/sessionBlock/exercises/exerciseUtils"
import { useBlockDuration } from "@modules/sessionBlock/hooks/useBlockDuration"
import { SessionBlockType } from "@modules/sessionBlock/sessionBlockTypes"
import { useSessionSidebar } from "@modules/sessions/hooks/useSessionSidebar"
import { FormikInput } from "@modules/ui/components/formikInput"
import { HUText } from "@modules/ui/components/huText"
import { displayedFullDateFormat } from "@modules/utils/dateUtils"
import { SimpleFormik } from "@src/typings/formik"
import dayjs from "dayjs"
import { useTheme } from "styled-components"

type ProgramInfiniteSessionFormProps = {
  formik: SimpleFormik<ProgramInfiniteSessionFormikType, ProgramSessionInputKey>
}

export const ProgramInfiniteSessionForm: React.FC<ProgramInfiniteSessionFormProps> = ({ formik }) => {
  const theme = useTheme()
  const { navigateToExerciseForm, navigateToNoteForCoachesForm, navigateToNoteForAthletesForm, navigateToMediaForm } =
    useSessionSidebar()
  const { timedBlocks, timedBlocksTotal } = useBlockDuration(formik.values.blocks)

  const addExercise = () => {
    const updatedBlocks = [
      ...formik.values.blocks,
      { type: SessionBlockType.SESSION_EXERCISE, content: GetNewExerciseDraft() },
    ]
    formik.setFieldValue("blocks", updatedBlocks)
    navigateToExerciseForm(true, updatedBlocks.length - 1)
  }

  const addCoachNote = () => {
    const updatedBlocks = [...formik.values.blocks, { type: SessionBlockType.COACH_NOTE }]
    formik.setFieldValue("blocks", updatedBlocks)
    navigateToNoteForCoachesForm(true, updatedBlocks.length - 1)
  }

  const addAthleteNoteBlock = () => {
    const updatedBlocks = [...formik.values.blocks, { type: SessionBlockType.ATHLETE_NOTE }]
    formik.setFieldValue("blocks", updatedBlocks)
    navigateToNoteForAthletesForm(true, updatedBlocks.length - 1)
  }

  const addMediaBlock = () => {
    const updatedBlocks = [...formik.values.blocks, { type: SessionBlockType.MEDIAS, content: { medias: [] } }]
    formik.setFieldValue("blocks", updatedBlocks)
    navigateToMediaForm(true, updatedBlocks.length - 1)
  }

  return (
    <div className="flex flex-column gap-5">
      {/* Session name */}
      <FormikInput
        flex
        formikStyle={{ flexGrow: 1, borderRadius: 30 }}
        isRequiredInput
        label={word("sessionForm.name.title")}
        getFieldProps={formik.getFieldProps}
        isInvalid={isFormFieldInValid("name", formik)}
        error={getFormErrorMessage("name", formik)}
        name="name"
      />

      {/* Session date */}
      <FormikInput
        flex
        isRequiredInput
        formikStyle={{ display: "flex", flexGrow: 1 }}
        label={word("sessionForm.date.title")}
        getFieldProps={formik.getFieldProps}
        isInvalid={isFormFieldInValid("date", formik)}
        error={getFormErrorMessage("date", formik, { errorMessage: word("sessionForm.error.date") })}
        name="date"
        type="calendar"
        calendarProps={{
          minDate: dayjs().toDate(),
          showIcon: true,
          dateFormat: displayedFullDateFormat,
          inputStyle: { textTransform: "capitalize" },
        }}
      />

      {/* Session tag selection */}
      <div className="flex flex-column gap-2">
        <HUText fontStyle="LS" color={theme.neutralColor700} className="uppercase">
          {word("sessionForm.tag.title")}
        </HUText>

        <div className="flex flex-row justify-content-between align-items-center">
          <SessionTagInput
            selectedTags={[
              { key: SessionTagEnum.gymnasticTag, value: formik.values.gymnasticTag },
              { key: SessionTagEnum.weightliftingTag, value: formik.values.weightliftingTag },
              { key: SessionTagEnum.cardioTag, value: formik.values.cardioTag },
            ]}
            onChangeSelectedTags={(tag: string, value: boolean) => {
              formik.setFieldValue(tag, value)
            }}
          />
        </div>
      </div>

      {/* Blocks */}
      <div className="flex flex-column gap-2">
        <div className="flex flex-row justify-content-between align-items-center">
          <HUText fontStyle="LS" color={theme.neutralColor700} className="uppercase">
            {`${word("program.planning.create.exercise.block_plural", { count: formik.values.blocks.length })} (${formik.values.blocks.length})`}
          </HUText>

          {timedBlocks.length > 0 && (
            <div>
              <HUText fontStyle="LS" color={theme.neutralColor700} className="uppercase border-right-1 border-300 pr-1">
                {`${word("sessionForm.blocks.timed")} (${timedBlocks.length})`}
              </HUText>
              <HUText fontStyle="LS" color={theme.neutralColor700} className="pl-1">
                {word("sessionForm.block.totalTime")}
                <HUText fontStyle="LS" className="pl-1 font-bold">
                  {timedBlocksTotal}
                </HUText>
                {word("global.minute.shortLabel", { count: timedBlocksTotal })}
              </HUText>
            </div>
          )}
        </div>

        {formik.values.blocks.map((block, i) => (
          <ExistingBlock block={block} blockIndex={i} key={i} />
        ))}

        {/* Add block */}
        <AddBlock
          onAddExerciseBlock={addExercise}
          onAddCoachNoteBlock={addCoachNote}
          onAddAthleteNoteBlock={addAthleteNoteBlock}
          onAddMediaBlock={addMediaBlock}
        />
      </div>

      {/* Hide session */}
      <FormikInput
        type="switchInline"
        label={word("session.form.hideSessionContent.label")}
        textSwitch={word("session.form.hideSessionContent.textSwitch")}
        description={word("session.form.hideSessionContent.description")}
        name="hideSessionContent"
        getFieldProps={formik.getFieldProps}
        isInvalid={isFormFieldInValid("hideSessionContent", formik)}
        error={getFormErrorMessage("hideSessionContent", formik)}
        setFieldValue={formik.setFieldValue}
        switchProps={{
          checked: formik.values.hideSessionContent,
        }}
      />
    </div>
  )
}
